import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectBackTeachers,
  tryGetAllTeachers,
  tryPreliquidation,
  selectPreLiquidation,
} from "../../../store/slices/backoffice-teachers";
import {
  Typography,
  Grid,
  Button,
  Drawer,
  IconButton,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  FormLabel,
  MenuItem,
  FormHelperText,
  DialogActions,
  Dialog,
  DialogTitle,
  Box,
  Stack,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useFormik } from "formik";
import ModalMessages from "../../layout/modal-messages";
import Spinner from "../../layout/spinner";
import { IsValidDate } from "../../../services/functions/validator";
import { PatternFormat } from "react-number-format";
import { FormatDate } from "../../../services/functions/formatDate";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const validate = (values) => {
  //Obtengo la fecha actual
  const today = FormatDate();
  const newDateFrom = FormatDate(values.dateFrom, "DD/MM/YYYY", null);
  const newDateTo = FormatDate(values.dateTo, "DD/MM/YYYY", null);

  const errors = {};

  if (
    (!values.teacherId || values.teacherId === "") &&
    values.teacherId !== 0
  ) {
    errors.teacherId = "Requerido";
  }

  //Fecha DESDE
  const isValidDateFrom = IsValidDate(values.dateFrom);
  if (!isValidDateFrom) {
    errors.dateFrom = "Ingrese una fecha válida en formato DD MM AAAA";
  }
  if (isValidDateFrom) {
    if (!values.dateFrom || values.dateFrom === "")
      errors.dateFrom = "Requerido";
    if (newDateFrom.isAfter(today))
      errors.dateFrom =
        "La fecha de inicio seleccionada no puede ser posterior a la fecha actual";
  }

  //Fecha HASTA
  const isValidDateTo = IsValidDate(values.dateTo);
  if (!isValidDateTo) {
    errors.dateTo = "Ingrese una fecha válida en formato DD MM AAAA";
  }
  if (isValidDateTo) {
    if (!values.dateTo || values.dateTo === "") errors.dateTo = "Requerido";
    if (newDateTo.isBefore(newDateFrom))
      errors.dateTo = "La Fecha de Fin no puede ser anterior a Fecha de Inicio";
  }

  if (
    !(
      newDateFrom.month() === newDateTo.month() &&
      newDateFrom.year() === newDateTo.year()
    )
  )
    errors.dateTo =
      "Fecha de Fin debe pertenecer al mismo mes que Fecha de Inicio";

  return errors;
};

const NewSettlementDrawer = ({
  openDrawer,
  setOpenDrawer,
  setOpenConfirmationDialog,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const selectTeachers = useSelector(selectBackTeachers);
  const { data: teachers } = selectTeachers;
  const newPreliquidation = useSelector(selectPreLiquidation);

  const [
    preliquidationConfirmationDialog,
    setPreliquidationConfirmationDialog,
  ] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (openDrawer) {
        try {
          await dispatch(tryGetAllTeachers());
        } catch (error) { }
      }
    };

    fetchData();
  }, [openDrawer]);

  //Creo el formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      teacherId: "",
      dateFrom: "",
      dateTo: "",
      isPreliminary: true,
    },
    validate,
    onSubmit: async (values) => {
      const request = {};
      request.teacherId = values.teacherId;
      request.dateFrom = FormatDate(values.dateFrom, "DD/MM/YYYY", "YYYY-MM-DDTHH:mm:ss.sssZ") //prettier-ignore
      request.dateTo = FormatDate(values.dateTo, "DD/MM/YYYY", "YYYY-MM-DDTHH:mm:ss.sssZ") //prettier-ignore
      request.isPreliminary = values.isPreliminary;

      const action = await dispatch(tryPreliquidation(request));
      if (action.payload.data.isValid) {
        setOpenConfirmationDialog(true);
        window.open(action.payload.data.data.file);
        setOpenDrawer(false);
      }
    },
  });

  useEffect(() => {
    formik.setValues({
      teacherId: "",
      dateFrom: "",
      dateTo: "",
      isPreliminary: true,
    });
  }, []);

  const settlementOptions = () => {
    let teachersAll = [...teachers];
    teachersAll.push({
      id: 0,
      description: "PRELIQUIDACIÓN MASIVA  🌐",
      shortDescription: "globalSettlement",
    });

    return teachersAll;
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={openDrawer}
      PaperProps={{
        sx: {
          zIndex: 1203,
          width: { xs: "100vw", sm: 500 },
          maxWidth: "100%",
          maxHeight: { xs: "90vh", sm: "100%" },
          borderRadius: { xs: "12px 12px 0 0", sm: 0 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
      onClose={() => setOpenDrawer(false)}
      sx={{
        zIndex: 1203,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#194B6F",
          color: "white",
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 1.5, sm: 3 },
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            color: "white",
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <Close />
        </IconButton>

        <Typography
          align="left"
          sx={{
            fontWeight: 700,
            fontSize: 18,
            mb: { xs: 0, sm: 1 },
            pr: 4,
          }}
        >
          Nueva Preliquidación
        </Typography>

        <Typography
          variant="body2"
          align="left"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          Por favor, complete información en cada campo que se solicite en el
          formulario
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "white",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Grid container rowSpacing={3} spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="settlementTo">Profesores</InputLabel>
              <Select
                name="settlementTo"
                label="Profesores"
                variant="outlined"
                size="small"
                fullWidth
                onChange={(e) => {
                  const { value } = e.target;
                  formik.setFieldValue("teacherId", value);
                }}
                value={formik.values.teacherId}
                error={formik.touched.teacherId && Boolean(formik.errors.teacherId)}
                autoComplete="off"
              >
                {teachers &&
                  settlementOptions()
                    .sort((a, b) => a.id - b.id)
                    .map((x) => {
                      return (
                        <MenuItem key={x.id} value={x.id}>
                          {x.description}
                        </MenuItem>
                      );
                    })}
              </Select>
              {formik.touched.teacherId && (
                <FormHelperText
                  htmlFor="teacherId"
                  error={formik.touched.teacherId && !!formik.errors.teacherId}
                >
                  {formik.errors.teacherId}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <InputLabel id="courseType" sx={{ fontSize: 13, mb: 0.5 }}>Fecha de Inicio</InputLabel>
            <PatternFormat
              id="initDate"
              label=""
              variant="outlined"
              size="small"
              fullWidth
              autoComplete="off"
              onChange={(e) => {
                const { value } = e.target;
                formik.setFieldValue("dateFrom", value);
              }}
              value={formik.values.dateFrom}
              error={formik.touched.dateFrom && Boolean(formik.errors.dateFrom)}
              helperText={formik.touched.dateFrom && formik.errors.dateFrom}
              customInput={TextField}
              format="##/##/####"
              mask="_"
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel id="courseType" sx={{ fontSize: 13, mb: 0.5 }}>Fecha de Fin</InputLabel>
            <PatternFormat
              id="endDate"
              label=""
              variant="outlined"
              size="small"
              autoComplete="off"
              fullWidth
              onChange={(e) => {
                const { value } = e.target;
                formik.setFieldValue("dateTo", value);
              }}
              value={formik.values.dateTo}
              error={formik.touched.dateTo && Boolean(formik.errors.dateTo)}
              helperText={formik.touched.dateTo && formik.errors.dateTo}
              customInput={TextField}
              format="##/##/####"
              mask="_"
            />
          </Grid>
          <Grid item xs={12} align="center">
            <FormControl>
              <FormLabel id="t" sx={{ fontWeight: "bold", color: "text.primary" }}>TIPO DE PRELIQUIDACION</FormLabel>
              <RadioGroup row aria-labelledby="settlement-label" name="aproval" sx={{ justifyContent: "center" }}>
                <FormControlLabel
                  value="Preliminar"
                  onChange={() => formik.setFieldValue("isPreliminary", true)}
                  control={<Radio />}
                  label="Preliminar"
                  sx={{ color: "black" }}
                  checked={formik.values.isPreliminary === true}
                />
                <FormControlLabel
                  value="Definitiva"
                  onChange={() => formik.setFieldValue("isPreliminary", false)}
                  control={<Radio />}
                  label="Definitiva"
                  sx={{ color: "black" }}
                  checked={formik.values.isPreliminary === false}
                />
              </RadioGroup>
              {formik.touched.isPreliminary && (
                <FormHelperText
                  htmlFor="isPreliminary"
                  error={
                    formik.touched.isPreliminary && !!formik.errors.isPreliminary
                  }
                  sx={{ textAlign: "center" }}
                >
                  {formik.errors.isPreliminary}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "center" }}>
            {isMobile ? (
              <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setPreliquidationConfirmationDialog(true)}
                  disabled={newPreliquidation?.isLoading}
                >
                  {newPreliquidation?.isLoading && <Spinner color="primary" />}
                  Generar
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={newPreliquidation.isLoading}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                <Button
                  variant="contained"
                  onClick={() => setPreliquidationConfirmationDialog(true)}
                  disabled={newPreliquidation?.isLoading}
                  sx={{ width: 100, marginRight: 2 }}
                >
                  {newPreliquidation?.isLoading && <Spinner color="primary" />}
                  Generar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={newPreliquidation.isLoading}
                  sx={{ border: 2, width: 100 }}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {newPreliquidation.errors && newPreliquidation.errors.length > 0 && (
        <ModalMessages errors={newPreliquidation.errors} />
      )}

      {preliquidationConfirmationDialog && (
        <Dialog
          open={preliquidationConfirmationDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {
              "¿Confirmas que los datos ingresados son correctos para iniciar el proceso de preliquidación?"
            }
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={() => {
                formik.handleSubmit();
                setPreliquidationConfirmationDialog(false);
              }}
              autoFocus
            >
              Aceptar
            </Button>
            <Button
              onClick={() => {
                setPreliquidationConfirmationDialog(false);
              }}
              color="error"
            >
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Drawer>
  );
};

export default NewSettlementDrawer;